// RenderRoutes.tsx
import React, { ElementType, useMemo } from "react";
import { Navigate, Route } from "react-router-dom";
import CommonContainer from "../../layouts/layoutnew/CommonContainer";
import CommonLayout from "../../layouts/commonLayout/CommonLayout";
import Error403 from "../../views/error403/Error403";
import { useSubscriptionExpired } from "../../hooks/useSubscriptionExpired";
import {
  AppRoute,
  filterDefaultRoutes,
} from "../routes/filterDefaultRoutes";

export type { AppRoute };
export { filterDefaultRoutes };

// Helper function to build full path
const buildPath = (parentPath: string, routePath: string): string => {
  // Remove trailing slash from parentPath
  const cleanParent = parentPath.endsWith("/") ? parentPath.slice(0, -1) : parentPath;
  // Ensure routePath doesn't start with /
  const cleanRoute = routePath.startsWith("/") ? routePath.slice(1) : routePath;
  return `${cleanParent}/${cleanRoute}`;
};

export const renderRoutes = (
  routes: AppRoute[] | undefined,
  parentPath = "",
  isInsideSubnav = false
): React.ReactElement[] => {
  if (!routes || routes.length === 0) {
    // If no routes and we're at top level, add catch-all error route
    if (!isInsideSubnav && parentPath) {
      return [
        <Route key="error-403-catchall" path="*" element={<Error403 />} />
      ];
    }
    return [];
  }

  const renderedRoutes = routes
    .map((route, i) => {
      const fullPath = buildPath(parentPath, route.PATH);
      const key = `${parentPath}-${i}-${route.PATH}`;

      // Check if route has SUBNAV and it has items
      if (route.SUBNAV && route.SUBNAV.length > 0) {
        // If DIRECT is true, create a container route that wraps all subnav items
        if (route.DIRECT) {
          // If route has ELEMENT, use that instead of CommonContainer
          if (route.ELEMENT) {
            const RouteElement = route.ELEMENT;
            return (
              <Route
                key={key}
                path={`${route.PATH}/*`}
                element={<RouteElement />}
              >
                {/* Default redirect to first subnav item */}
                <Route
                  index
                  element={
                    <Navigate
                      to={route.SUBNAV[0]?.PATH || ""}
                      replace
                    />
                  }
                />
                {/* Recursively render nested routes with full path context */}
                {renderRoutes(route.SUBNAV, fullPath, true)}
                {/* Catch-all route for unauthorized access in nested routes */}
                <Route path="*" element={<Error403 />} />
              </Route>
            );
          }
          // If no ELEMENT, use CommonContainer
          // Keep SUBNAV paths relative for CommonContainer NavLink
          // CommonContainer expects relative paths in SUBNAV
          return (
            <Route
              key={key}
              path={`${route.PATH}/*`}
              element={
                <CommonContainer
                  SUBNAV={route.SUBNAV}
                  DISABLED={route.DISABLED}
                  MAINTITLE={route.MAINTITLE || route.TITLE}
                />
              }
            >
              {/* Default redirect to first subnav item */}
              <Route
                index
                element={
                  <Navigate
                    to={route.SUBNAV[0]?.PATH || ""}
                    replace
                  />
                }
              />
              {/* Recursively render nested routes with full path context */}
              {renderRoutes(route.SUBNAV, fullPath, true)}
              {/* Catch-all route for unauthorized access in nested routes */}
              <Route path="*" element={<Error403 />} />
            </Route>
          );
        } else {
          // If not DIRECT, render each subnav item as a separate route
          return route.SUBNAV.map((subRoute: AppRoute, j: number) => {
            const subFullPath = buildPath(fullPath, subRoute.PATH);
            const subKey = `${key}-${j}`;

            // Check if subRoute has nested SUBNAV (recursive)
            if (subRoute.SUBNAV && subRoute.SUBNAV.length > 0) {
              // If subRoute has ELEMENT, use that instead of CommonContainer
              if (subRoute.ELEMENT) {
                const SubRouteElement = subRoute.ELEMENT;
                return (
                  <Route
                    key={subKey}
                    path={`${subRoute.PATH}/*`}
                    element={<SubRouteElement />}
                  >
                    {/* Default redirect to first nested subnav item */}
                    <Route
                      index
                      element={
                        <Navigate
                          to={subRoute.SUBNAV[0]?.PATH || ""}
                          replace
                        />
                      }
                    />
                    {/* Recursively render nested routes */}
                    {renderRoutes(subRoute.SUBNAV, subFullPath, true)}
                    {/* Catch-all route for unauthorized access in nested routes */}
                    <Route path="*" element={<Error403 />} />
                  </Route>
                );
              }
              // If no ELEMENT, use CommonContainer
              // Keep nested SUBNAV paths relative for CommonContainer
              return (
                <Route
                  key={subKey}
                  path={`${subRoute.PATH}/*`}
                  element={
                    <CommonContainer
                      SUBNAV={subRoute.SUBNAV}
                      DISABLED={subRoute.DISABLED}
                      MAINTITLE={subRoute.MAINTITLE || subRoute.TITLE}
                    />
                  }
                >
                  {/* Default redirect to first nested subnav item */}
                  <Route
                    index
                    element={
                      <Navigate
                        to={subRoute.SUBNAV[0]?.PATH || ""}
                        replace
                      />
                    }
                  />
                  {/* Recursively render nested routes */}
                  {renderRoutes(subRoute.SUBNAV, subFullPath, true)}
                  {/* Catch-all route for unauthorized access in nested routes */}
                  <Route path="*" element={<Error403 />} />
                </Route>
              );
            }

            // Render subRoute with ELEMENT if it exists
            if (subRoute.ELEMENT) {
              const SubRouteElement = subRoute.ELEMENT;
              return (
                <Route
                  key={subKey}
                  path={subRoute.PATH}
                  element={<SubRouteElement />}
                />
              );
            }

            return null;
          }).filter(Boolean) as React.ReactElement[];
        }
      }

      // Render route with ELEMENT if it exists
      // Don't wrap in CommonLayout if inside SUBNAV or if route has SUBNAV
      if (route.ELEMENT) {
        const RouteElement = route.ELEMENT;
        // Only use CommonLayout if NOT inside SUBNAV and route has no SUBNAV
        if (!isInsideSubnav && (!route.SUBNAV || route.SUBNAV.length === 0)) {
          return (
            <Route
              key={key}
              path={route.PATH}
              element={
                <CommonLayout>
                  <RouteElement />
                </CommonLayout>
              }
            />
          );
        } else {
          // Render without CommonLayout if inside SUBNAV or route has SUBNAV
          return (
            <Route
              key={key}
              path={route.PATH}
              element={<RouteElement />}
            />
          );
        }
      }

      return null;
    })
    .filter(Boolean) as React.ReactElement[];

  // Add catch-all error route at the end (only at top level, not in nested routes)
  if (!isInsideSubnav && parentPath) {
    return [
      ...renderedRoutes,
      <Route key="error-403-catchall" path="*" element={<Error403 />} />
    ];
  }

  return renderedRoutes;
};

/** Routes available in the UI — full set when active, none when payment is overdue. */
export function getActiveClientRoutes(
  routes: AppRoute[],
  subscriptionExpired: boolean
): AppRoute[] {
  return subscriptionExpired ? [] : routes;
}

/** Hook: permission-filtered routes, narrowed to DEFAULT_ROUTE when subscription expired. */
export function useActiveClientRoutes(routes: AppRoute[]): AppRoute[] {
  const { expired: subscriptionExpired } = useSubscriptionExpired();
  return useMemo(
    () => getActiveClientRoutes(routes, subscriptionExpired),
    [routes, subscriptionExpired]
  );
}
